import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';

import { BibliographicResource } from '®app/shared/bibliographic-resource';

import {
  ResourceFormatter,
  ResourceMetadata,
  ResourceMetadataResult,
  formatArray,
  formatString,
  i18n,
} from './item-metadata.utils';

@Component({
  selector: 'rl-item-metadata',
  templateUrl: './item-metadata.component.html',
})
export class ItemMetadataComponent implements OnInit, OnChanges {
  @Input() depth = 0;
  @Input() resource: BibliographicResource;

  resourceMetadata: ResourceMetadataResult | null;
  partOfMetadata: ResourceMetadataResult | null;

  /**
   * Get the aria level for the "Details" heading.
   * This will be one level higher than the item heading level,
   * which must begin at level 2 since the page title uses <h1>
   *
   * - Page title (level = 1)
   * ...
   * - - Resource title (level = depth + 2)
   * - - - Details (level = depth + 3)
   *
   * @returns number
   */
  get ariaHeadingLevel(): number {
    return this.depth + 3;
  }

  ngOnInit() {
    this.resourceMetadata = this.createResourceMetadata(this.resource);
    this.partOfMetadata = this.createResourceMetadata(
      this.resource.part_of,
      true,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const {
      previousValue,
      currentValue,
    }: {
      previousValue: BibliographicResource;
      currentValue: BibliographicResource;
    } = changes['resource'];

    if (previousValue !== currentValue) {
      this.resourceMetadata = this.createResourceMetadata(currentValue);
      this.partOfMetadata = this.createResourceMetadata(
        currentValue.part_of,
        true,
      );
    }
  }

  private createResourceMetadata(
    resource?: BibliographicResource,
    partOf?: boolean,
  ): ResourceMetadataResult | null {
    if (!resource) {
      return null;
    }

    const formatters: ResourceFormatter[] = [
      res => formatArray(i18n.authors, res.authorNames, '; '),
      res => formatArray(i18n.editorNames, res.editorNames, '; '),
      res => formatArray(i18n.dates, res.formattedDates, '; '),
      res => formatArray(i18n.publisher, res.publisher_names, '; '),
      res => formatArray(i18n.places, res.places_of_publication, '; '),
      res => formatString(i18n.edition, res.edition),
      res => formatString(i18n.volume, res.volume),
      res => formatString(i18n.issue, res.issue),
      res => formatString(i18n.pages, res.pages),
      res => formatArray(i18n.issns, res.issns),
      res => formatArray(i18n.eissns, res.eissns),
      res => formatArray(i18n.isbns, res.ownIsbns),
      res => formatString(i18n.format, res.format),
      res => formatString(i18n.doi, res.doi),
      res => formatString(i18n.location, res.location),
      res => formatString(i18n.court, res.court),
      res => formatString(i18n.series, res.series),
      res => formatString(i18n.number, res.number),
      res => formatString(i18n.identifier, res.identifier),
    ];

    if (partOf) {
      const formattersPartOf: ResourceFormatter[] = [
        res => formatString(i18n.title, res.title),
      ];
      formatters.push(...formattersPartOf);
    }

    const metadata = formatters
      .map(fn => fn(resource))
      .filter((result): result is ResourceMetadata => result !== null);

    if (metadata.length === 0) {
      return null;
    }

    return metadata;
  }
}
